import {
  Box,
  DistributionBar,
  QuoteStatusEnum,
  toBigWithDefault,
  type ColumnDef,
  type DistributionNamedSection,
} from '@talos/kyoko';
import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import Big from 'big.js';
import {
  isCareOrderRow,
  isLegRow,
  isOrderRow,
  isQuoteRow,
  type CareOrderBlotterEntity,
  type CareOrderRow,
  type LegRow,
  type OrderRow,
  type QuoteRow,
} from '../types';
import {
  doneOrdStatuses,
  PROGRESS_COLORS,
  workingOrdStatuses,
  workingQuoteStatuses,
  type CareOrderProgressBar,
} from '../utils';
import { legRowFadedCellClassRules } from './legRowFadedCellClassRules';

type CareOrderProgressColumnValue = readonly CareOrderProgressBar[];
const CARE_ORDER_PROGRESS_COLUMN_ID = 'progress';

function getPercent(value: Big, totalQty: Big) {
  return totalQty.gt(0) ? value.div(totalQty) : Big(0);
}

function getCareOrderProgressBars(data: CareOrderRow) {
  const orderQty = toBigWithDefault(data.OrderQty, 0);
  const doneQty = toBigWithDefault(data.doneQty, 0);
  const pendingFixing = toBigWithDefault(data.pendingFixingQty, 0);
  const working = toBigWithDefault(data.WorkingQty, 0);

  return [
    {
      id: 'done',
      valueNotional: doneQty,
      valuePercent: getPercent(doneQty, orderQty),
      color: PROGRESS_COLORS.done,
      appearance: 'filled',
    },
    {
      id: 'pendingFixing',
      valueNotional: pendingFixing,
      valuePercent: getPercent(pendingFixing, orderQty),
      color: PROGRESS_COLORS.pendingFixing,
      appearance: 'filled',
    },
    {
      id: 'working',
      valueNotional: working,
      valuePercent: getPercent(working, orderQty),
      color: PROGRESS_COLORS.working,
      appearance: 'striped',
    },
  ] as const satisfies readonly CareOrderProgressBar[];
}

/**
 * RFQ/leg bars reflect the current quote status. Open statuses render as full-width status indicators,
 * while pending-fix and filled statuses use TradedQty / OrderQty so size-countered fills render partially.
 */
export function getQuoteProgressBars(data: Pick<QuoteRow | LegRow, 'OrderQty' | 'QuoteStatus' | 'TradedQty'>) {
  if (workingQuoteStatuses.includes(data.QuoteStatus)) {
    return [
      {
        id: 'working',
        valueNotional: Big(1),
        valuePercent: Big(1),
        color: PROGRESS_COLORS.working,
        appearance: 'striped',
      },
    ] as const satisfies readonly CareOrderProgressBar[];
  }

  if (data.QuoteStatus === QuoteStatusEnum.PendingFix || data.QuoteStatus === QuoteStatusEnum.Filled) {
    const id = data.QuoteStatus === QuoteStatusEnum.PendingFix ? 'pendingFixing' : 'done';
    const orderQty = toBigWithDefault(data.OrderQty, 0);
    const tradedQty = toBigWithDefault(data.TradedQty, 0);
    return [
      {
        id,
        valueNotional: tradedQty,
        valuePercent: getPercent(tradedQty, orderQty),
        color: PROGRESS_COLORS[id],
        appearance: 'filled',
      },
    ] as const satisfies readonly CareOrderProgressBar[];
  }

  return [] as const satisfies readonly CareOrderProgressBar[];
}

export function getOrderProgressBars(data: Pick<OrderRow, 'CumQty' | 'OrderQty' | 'OrdStatus'>) {
  const orderQty = toBigWithDefault(data.OrderQty, 0);

  if (workingOrdStatuses.includes(data.OrdStatus)) {
    const filledQty = toBigWithDefault(data.CumQty, 0);
    const remainingQty = orderQty.minus(filledQty);
    const working = remainingQty.gt(0) ? remainingQty : Big(0);

    return [
      {
        id: 'done',
        valueNotional: filledQty,
        valuePercent: getPercent(filledQty, orderQty),
        color: PROGRESS_COLORS.done,
        appearance: 'filled',
      },
      {
        id: 'working',
        valueNotional: working,
        valuePercent: getPercent(working, orderQty),
        color: PROGRESS_COLORS.working,
        appearance: 'striped',
      },
    ] as const satisfies readonly CareOrderProgressBar[];
  }

  if (!doneOrdStatuses.includes(data.OrdStatus)) {
    return [] as const satisfies readonly CareOrderProgressBar[];
  }

  return [
    {
      id: 'done',
      valueNotional: orderQty,
      valuePercent: getPercent(orderQty, orderQty),
      color: PROGRESS_COLORS.done,
      appearance: 'filled',
    },
  ] as const satisfies readonly CareOrderProgressBar[];
}

// Used by column valueGetter directly, decoupled from aggrid internals. Helps testing.
export function getCareOrderProgressValue(params: { data: CareOrderBlotterEntity }): CareOrderProgressColumnValue {
  const { data } = params;
  if (isCareOrderRow(data)) {
    return getCareOrderProgressBars(data);
  }

  if (isQuoteRow(data) || isLegRow(data)) {
    // Quotes and their legs are treated the exact same
    return getQuoteProgressBars(data);
  }

  if (isOrderRow(data)) {
    return getOrderProgressBars(data);
  }

  return [];
}

// Used by column valueFormatter directly, decoupled from aggrid internals. Helps testing.
export function getCareOrderProgressFormattedValue(value: CareOrderProgressColumnValue | null | undefined) {
  if (value == null || value.length === 0) {
    return '';
  }

  const percent = value.reduce((total, bar) => total.plus(bar.valuePercent), Big(0));

  return `${percent.times(100).toFixed(2)}%`;
}

export const getCareOrderProgressColumn = (): ColumnDef<CareOrderBlotterEntity> => {
  return {
    type: 'custom',
    id: CARE_ORDER_PROGRESS_COLUMN_ID,
    pinned: 'left',
    params: {
      valueGetter: (params: ValueGetterParams<CareOrderBlotterEntity>): CareOrderProgressColumnValue => {
        if (!params.data) {
          return [];
        }

        return getCareOrderProgressValue({ data: params.data });
      },
      valueFormatter: (params: ValueFormatterParams<CareOrderBlotterEntity>) =>
        getCareOrderProgressFormattedValue(params.value),
      cellClassRules: legRowFadedCellClassRules,
      cellRenderer: AgCareOrderProgress,
      minWidth: 120,
      maxWidth: 480,
      width: 120,
    },
  };
};

function AgCareOrderProgress(params: ICellRendererParams<CareOrderBlotterEntity>) {
  return (
    <Box w="100%">
      <DistributionBar
        sections={progressBarsToSections(params.value)}
        showHeaders={false}
        showRemainder={true}
        showLabel={true}
        label={params.valueFormatted}
        size="small"
      />
    </Box>
  );
}

function progressBarsToSections(bars: CareOrderProgressBar[]): DistributionNamedSection[] {
  return bars
    .filter(bar => bar.valueNotional.gt(0))
    .map(bar => ({
      id: bar.id,
      header: null,
      parts: [
        {
          id: bar.id,
          color: bar.color,
          value: bar.valuePercent,
          appearance: bar.appearance,
        },
      ],
    }));
}
